import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { sesamePasswordLogin, sesameDisconnect } from '../services/sesameAuthService';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'project_manager';
  tenantId: string;
  permissions: string[];
  isMegaAdmin: boolean;
  authUserId: string;
}

interface TenantUserUpdate {
  tenantId: string;
  appUserId: string;
  role: User['role'];
}

interface AuthContextType {
  user: User | null;
  login: (identifier: string, password: string) => Promise<void>;
  loginWithSession: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUserForTenant: (update: TenantUserUpdate) => void;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

async function checkMegaAdminFromDatabase(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('check_mega_admin_status');
    if (error) {
      console.error('Error checking mega admin status:', error);
      return false;
    }
    return data === true;
  } catch {
    return false;
  }
}

const getDefaultPermissions = (): string[] => {
  return [
    'contests:read',
    'contests:write',
    'rewards:read',
    'rewards:write',
    'analytics:read'
  ];
};

interface AppUserRecord {
  id: string;
  tenant_id: string;
  auth_user_id: string;
  name: string;
  email: string;
  role: string;
}

async function fetchAppUserByAuthId(authUserId: string): Promise<AppUserRecord | null> {
  const { data, error } = await supabase
    .from('app_users')
    .select('id, tenant_id, auth_user_id, name, email, role')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching app_user:', error);
    return null;
  }

  return data;
}

async function buildUserFromSupabaseUser(supabaseUser: SupabaseUser): Promise<User> {
  const isMegaAdmin = await checkMegaAdminFromDatabase();

  const appUser = await fetchAppUserByAuthId(supabaseUser.id);

  if (appUser) {
    const normalizedRole = appUser.role === 'owner' ? 'admin' : appUser.role;
    return {
      id: appUser.id,
      email: appUser.email,
      name: appUser.name,
      role: normalizedRole as User['role'],
      tenantId: appUser.tenant_id,
      permissions: getDefaultPermissions(),
      isMegaAdmin,
      authUserId: supabaseUser.id
    };
  }

  const userRole = supabaseUser.user_metadata?.role || 'admin';
  const normalizedRole = userRole === 'owner' ? 'admin' : userRole;

  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    name: supabaseUser.user_metadata?.name || supabaseUser.email || 'User',
    role: normalizedRole,
    tenantId: supabaseUser.user_metadata?.tenantId || '',
    permissions: getDefaultPermissions(),
    isMegaAdmin,
    authUserId: supabaseUser.id
  };
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastAuthUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const mappedUser = await buildUserFromSupabaseUser(session.user);
        lastAuthUserIdRef.current = session.user.id;
        setUser(mappedUser);
      }
      setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' && session?.user?.id === lastAuthUserIdRef.current) {
        return;
      }

      (async () => {
        if (session?.user) {
          const mappedUser = await buildUserFromSupabaseUser(session.user);
          lastAuthUserIdRef.current = session.user.id;
          setUser(mappedUser);
        } else {
          lastAuthUserIdRef.current = null;
          setUser(null);
        }
      })();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (identifier: string, password: string) => {
    setIsLoading(true);
    try {
      await sesamePasswordLogin(identifier, password);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithSession = async (accessToken: string, refreshToken: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) throw error;

      if (data.user) {
        const mappedUser = await buildUserFromSupabaseUser(data.user);
        setUser(mappedUser);
      }
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Session login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await sesameDisconnect();
    await supabase.auth.signOut();
    setUser(null);
  };

  const refreshUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const mappedUser = await buildUserFromSupabaseUser(session.user);
      lastAuthUserIdRef.current = session.user.id;
      setUser(mappedUser);
    }
  };

  const updateUserForTenant = (update: TenantUserUpdate) => {
    if (!user) return;

    setUser({
      ...user,
      id: update.appUserId,
      tenantId: update.tenantId,
      role: update.role
    });
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithSession, logout, updateUserForTenant, refreshUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
